import { Platform, StyleSheet, StyleProp, ViewStyle, DimensionValue } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { useState, useEffect } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  SharedValue,
  runOnJS,
  interpolate,
  Extrapolation,
  withSpring,
  Easing,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";

export type VerticalScrollbarProps = {
  scrollY: SharedValue<number>; // Current scroll position
  contentHeight: number; // Total scrollable content height
  containerHeight: number; // Visible container height
  onScroll?: (scrollY: number) => void; // Callback for scroll changes
  style?: StyleProp<ViewStyle>;
  thumbStyle?: StyleProp<ViewStyle>;
  trackStyle?: StyleProp<ViewStyle>;
  autoHide?: boolean; // Default true
  thumbMinHeight?: number; // Minimum thumb height
  width?: number; // Scrollbar width
  topMargin?: DimensionValue;
  bottomMargin?: DimensionValue;
  right?: DimensionValue; // Distance from right
};

export default function VerticalScrollbar({
  scrollY,
  contentHeight,
  containerHeight,
  onScroll,
  style,
  thumbStyle,
  trackStyle,
  autoHide = true,
  thumbMinHeight = 30,
  width = 12,
  topMargin = 0,
  bottomMargin = 0,
}: VerticalScrollbarProps) {
  const { colors } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Animation values
  const opacity = useSharedValue(autoHide ? 0 : 0.8);
  const trackOpacity = useSharedValue(0.3);

  // Calculate scrollbar dimensions
  const maxScrollY = Math.max(0, contentHeight - containerHeight);

  // Calculate actual usable height considering bottomMargin
  const bottomMarginValue = typeof bottomMargin === "number" ? bottomMargin : 0;
  const availableHeight = containerHeight - bottomMarginValue;
  const trackHeight = availableHeight - 20; // 10px padding on each side

  // Simple scrollbar logic: thumb size represents visible portion of content
  const thumbHeight = Math.max(thumbMinHeight, trackHeight * (containerHeight / contentHeight));
  const maxThumbY = trackHeight - thumbHeight;

  // Show/hide logic
  useEffect(() => {
    if (!autoHide) return;

    if (isDragging || isHovered) {
      opacity.value = withSpring(1, { damping: 20, stiffness: 300 });
      trackOpacity.value = withTiming(0.5, { duration: 200 });
    } else {
      const timer = setTimeout(() => {
        opacity.value = withTiming(0.7, {
          duration: 400,
          easing: Easing.out(Easing.cubic),
        });
        trackOpacity.value = withTiming(0.2, { duration: 400 });
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [isDragging, isHovered, autoHide, opacity, trackOpacity]);

  // Animated styles
  const containerAnimatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateX: withSpring(0, { damping: 15 }) }],
  }));

  const trackAnimatedStyle = useAnimatedStyle(() => ({
    opacity: trackOpacity.value,
  }));

  const thumbAnimatedStyle = useAnimatedStyle(() => {
    const thumbY = interpolate(scrollY.value, [0, maxScrollY], [0, maxThumbY], Extrapolation.CLAMP);

    return {
      transform: [{ translateY: thumbY }],
      elevation: isDragging ? 8 : 4,
    };
  });

  // Gesture handling
  const panGesture = Gesture.Pan()
    .onChange((event) => {
      // Convert thumb movement to scroll movement
      // If thumb moves its full range (maxThumbY), content should scroll its full range (maxScrollY)
      const thumbMovementRatio = maxScrollY / maxThumbY; // How much content scrolls per thumb pixel
      const deltaScroll = event.changeY * thumbMovementRatio;
      const newScrollY = Math.max(0, Math.min(maxScrollY, scrollY.value + deltaScroll));

      scrollY.value = newScrollY;
      if (onScroll) {
        runOnJS(onScroll)(newScrollY);
      }
    })
    .onBegin(() => {
      runOnJS(setIsDragging)(true);
      // No scaling - just state change for color feedback
    })
    .onEnd(() => {
      runOnJS(setIsDragging)(false);
    });

  // Hover handlers for web
  const handleHoverIn = () => {
    if (Platform.OS === "web") {
      setIsHovered(true);
      // No scaling on hover, just state change for opacity
    }
  };

  const handleHoverOut = () => {
    if (Platform.OS === "web") {
      setIsHovered(false);
    }
  };

  // Always render container to prevent layout jumps, but conditionally render content
  const shouldShowScrollbar = contentHeight > containerHeight;

  return (
    <Animated.View
      style={[
        styles.container,
        {
          width,
          marginTop: topMargin,
          height: availableHeight, // Use calculated height instead of marginBottom
        },
        containerAnimatedStyle,
        style,
      ]}
    >
      {shouldShowScrollbar && (
        <>
          {/* Track */}
          <Animated.View
            style={[
              styles.track,
              {
                backgroundColor: colors.border,
                height: trackHeight,
                width: Platform.OS === "web" ? width : width * 0.3,
              },
              trackAnimatedStyle,
              trackStyle,
            ]}
          />

          {/* Thumb */}
          <GestureDetector gesture={panGesture}>
            <Animated.View
              onPointerEnter={handleHoverIn}
              onPointerLeave={handleHoverOut}
              style={[
                //@ts-ignore
                styles.thumb,
                {
                  height: thumbHeight,
                  width: Platform.OS === "web" ? width * 0.8 : width * 0.7,
                  backgroundColor: isDragging ? colors.primary : colors.border,
                  opacity: isDragging ? 1 : 0.8,
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.2)",
                },
                thumbAnimatedStyle,
                thumbStyle,
              ]}
            />
          </GestureDetector>
        </>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: "flex-start",
    alignItems: "center",
    paddingVertical: 10,
    zIndex: 20,
    elevation: 20,
  },
  track: {
    position: "absolute",
    borderRadius: 4,
    top: 10,
    ...Platform.select({
      web: {
        // background: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.1), transparent)",
      },
    }),
  },
  thumb: {
    position: "absolute",
    borderRadius: 6,
    top: 10,
    cursor: Platform.OS === "web" ? "grab" : undefined,
    // Subtle border for definition
    borderWidth: Platform.OS === "web" ? 0.5 : 0,
    borderColor: "rgba(255,255,255,0.1)",
  },
});
